/**
 * Transit vehicle bodies, second pass: the drop can (descent + crashed), the
 * delivery lander and its crate, and the pickup ferry (flight + landed). Only the
 * static bodies live here; flames, exhaust particles, scorch/target discs and the
 * animation state machines stay in dropCan.ts / droneDelivery.ts / ferry.ts and
 * attach these groups as before.
 *
 * Units are model-local; the effects scale them by planet radius (drop can
 * 0.30·R, lander 0.15·R, ferry per its effect). Lambert materials only, so the
 * crashed-can scenery bakes with everything else.
 *
 * Design reference: design_sheets/transit.html
 */

import * as THREE from 'three';
import type { TeamPalette } from '../types';
import { KIT, KitRng, mat, matE, box, cyl, sphere, torus, at, group, scorch, rubble } from './buildingKit';

const M = { metal: '#6c7078', metalDk: '#3a3c44', metalLt: '#b8bcc4', shield: '#3a3028', shieldBurnt: '#1a1210', foil: '#b8a040', foilDk: '#8a7030', glass: '#1c222c', navRed: '#ff4a3a', navGreen: '#3aff6a' };

/** Hull plate: thin raised panel with a darker seam ring, for breaking up large surfaces. */
function plate(w: number, h: number, c: string): THREE.Mesh { return box(w, h, 0.012, mat(c)); }

// ---------------------------------------------------------------------------
// DROP CAN
// ---------------------------------------------------------------------------

/** Descent configuration: tapered can on a heat shield, retro nozzles, team band, hatch outline. */
export function buildDropCanDescentBody(team: TeamPalette): THREE.Group {
  const g = new THREE.Group();
  g.add(at(cyl(0.22, 0.32, 0.7, mat(team.primary), 14), 0, 0.55, 0));                         // tapered body
  g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.22, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(team.accent)), 0, 0.9, 0));   // dome cap
  g.add(at(cyl(0.225, 0.225, 0.03, mat(team.detail), 14), 0, 0.905, 0));                     // cap seam
  g.add(at(cyl(0.05, 0.06, 0.08, mat(M.metalDk), 8), 0, 1.14, 0), at(sphere(0.02, matE(team.visor, 0.6), 8, 6), 0, 1.2, 0));   // beacon mast + light
  g.add(at(cyl(0.255, 0.29, 0.06, mat(team.visor), 14), 0, 0.72, 0));                        // team band
  g.add(at(cyl(0.3, 0.315, 0.03, mat(team.detail), 14), 0, 0.37, 0));                        // lower seam
  // ablative heat shield: dark disc with segment plates
  g.add(at(cyl(0.32, 0.34, 0.05, mat(M.shield), 14), 0, 0.19, 0));
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.add(at(box(0.12, 0.012, 0.09, mat('#4a3c32')), Math.cos(a) * 0.22, 0.167, Math.sin(a) * 0.22, 0, -a, 0)); }
  // retro nozzles with brackets
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    g.add(at(cyl(0.025, 0.045, 0.07, mat(M.metal), 8), Math.cos(a) * 0.24, 0.16, Math.sin(a) * 0.16 + Math.sin(a) * 0.08));
    g.add(at(box(0.03, 0.06, 0.03, mat(M.metalDk)), Math.cos(a) * 0.26, 0.24, Math.sin(a) * 0.26));
  }
  // hatch outline + porthole + handles + company plate
  g.add(at(plate(0.16, 0.26, team.accent), 0, 0.55, 0.285, -0.14));
  g.add(at(torus(0.035, 0.008, mat(M.metalLt), 6, 14), 0, 0.64, 0.3, -0.14), at(new THREE.Mesh(new THREE.CircleGeometry(0.03, 12), matE(team.visor, 0.5)), 0, 0.64, 0.302, -0.14));
  for (const sy of [0.45, 0.72]) g.add(at(box(0.08, 0.015, 0.03, mat(M.metalLt)), 0.2, sy, 0.22, 0, 0.6, 0));
  g.add(at(plate(0.1, 0.05, team.detail), -0.17, 0.6, 0.24, 0, -0.6, 0));
  return g;
}

/** After landing: same can, tilted and scorched, hatch blown, debris. Deterministic per team. */
export function buildDropCanCrashedBody(team: TeamPalette, seed = 0): THREE.Group {
  const rng = new KitRng(101 + seed * 7);
  const g = new THREE.Group();
  const can = new THREE.Group();
  can.add(at(cyl(0.22, 0.32, 0.7, mat(team.primary), 14), 0, 0.35, 0));
  can.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.22, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(team.accent)), 0, 0.7, 0));
  can.add(at(cyl(0.225, 0.225, 0.03, mat(team.detail), 14), 0, 0.705, 0));
  can.add(at(cyl(0.255, 0.29, 0.06, mat(team.visor), 14), 0, 0.52, 0));
  can.add(at(cyl(0.32, 0.34, 0.05, mat(M.shieldBurnt), 14), 0, 0.0, 0));
  can.add(at(box(0.14, 0.22, 0.03, mat(KIT.glassDark)), 0, 0.4, 0.3, -0.14));                   // open hatchway
  can.add(at(cyl(0.05, 0.06, 0.08, mat(M.metalDk), 8), 0.06, 0.93, 0, 0, 0, 0.5));              // bent beacon mast
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; can.add(at(cyl(0.025, 0.045, 0.07, mat(M.metalDk), 8), Math.cos(a) * 0.24, -0.02, Math.sin(a) * 0.24)); }
  for (let i = 0; i < 3; i++) can.add(at(box(0.08, 0.1, 0.012, mat('#2a2420')), Math.cos(i * 2.1) * 0.29, 0.25 + i * 0.1, Math.sin(i * 2.1) * 0.29, 0, -i * 2.1, 0));   // scorch streaks
  at(can, 0.0, 0.06, 0, 0.08, 0, 0.15);
  g.add(can);
  g.add(at(box(0.14, 0.22, 0.02, mat(team.accent)), 0.42, 0.012, 0.18, Math.PI / 2 - 0.1, 0.4, 0));   // blown hatch
  g.add(at(box(0.02, 0.26, 0.02, mat(M.metalLt)), 0.3, 0.14, 0.26, 0.3, 0, 0.9));                     // deployed ladder rail
  g.add(scorch(0.5), rubble(team, rng, 6, 0.45));
  return g;
}

// ---------------------------------------------------------------------------
// DELIVERY LANDER (drone) + CRATE
// ---------------------------------------------------------------------------

/** Lander: gold-foil descent stage on four legs, grey ascent cabin, RCS, dish, landing lights. */
export function buildLanderBody(): THREE.Group {
  const r = new THREE.Group();
  r.add(at(cyl(0.18, 0.2, 0.14, mat(M.foil), 8), 0, 0.12, 0));
  for (const y of [0.08, 0.16]) r.add(at(cyl(0.186, 0.206, 0.014, mat(M.foilDk), 8), 0, y, 0));
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2 + Math.PI / 8; r.add(at(box(0.05, 0.1, 0.008, mat(i % 2 ? M.foilDk : '#c8b050')), Math.cos(a) * 0.19, 0.12, Math.sin(a) * 0.19, 0, -a, 0)); }   // foil panels
  r.add(at(box(0.2, 0.16, 0.18, mat('#a0a0a0')), 0, 0.27, 0));                                   // cabin
  for (const sx of [-1, 1]) r.add(at(box(0.012, 0.14, 0.16, mat('#8a8a90')), sx * 0.1, 0.27, 0)); // cabin side panels
  r.add(at(box(0.15, 0.1, 0.006, mat('#606868')), 0, 0.28, 0.093), at(box(0.04, 0.04, 0.008, mat(M.glass)), -0.03, 0.3, 0.097, 0, 0, 0.15), at(box(0.04, 0.04, 0.008, mat(M.glass)), 0.03, 0.3, 0.097, 0, 0, -0.15));
  r.add(at(cyl(0.04, 0.04, 0.04, mat('#888888'), 8), 0, 0.37, 0), at(torus(0.045, 0.006, mat(M.metalLt), 6, 12), 0, 0.39, 0, Math.PI / 2));   // docking port
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; r.add(at(box(0.03, 0.04, 0.03, mat(M.metal)), Math.cos(a) * 0.12, 0.27, Math.sin(a) * 0.12)); for (const k of [-1, 1]) r.add(at(cyl(0.006, 0.009, 0.015, mat(M.metalDk), 6), Math.cos(a) * 0.135, 0.27 + k * 0.012, Math.sin(a) * 0.135, Math.PI / 2, a, 0)); }   // RCS quads
  r.add(at(cyl(0.005, 0.005, 0.12, mat('#909090'), 4), 0.08, 0.41, 0.05, 0, 0, 0.15));
  const dish = group(at(new THREE.Mesh(new THREE.SphereGeometry(0.045, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), mat(M.metalLt)), 0, 0, 0, Math.PI * 0.75), at(cyl(0.004, 0.004, 0.04, mat(M.metalLt), 4), 0, 0.01, 0.02, 0.6));
  r.add(at(dish, -0.09, 0.4, -0.04));
  r.add(at(cyl(0.06, 0.09, 0.06, mat('#4a4a4a'), 10), 0, 0.02, 0), at(new THREE.Mesh(new THREE.CircleGeometry(0.05, 10), mat(KIT.scorch)), 0, -0.011, 0, Math.PI / 2));   // engine bell
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const leg = new THREE.Group();
    leg.add(at(cyl(0.008, 0.008, 0.3, mat('#888888'), 4), 0.06, -0.1, 0, 0, 0, 0.5));
    leg.add(at(cyl(0.005, 0.005, 0.22, mat('#888888'), 4), 0.08, -0.02, 0, 0, 0, 0.8));
    leg.add(at(cyl(0.04, 0.045, 0.008, mat(M.metal), 8), 0.15, -0.2, 0), at(cyl(0.002, 0.002, 0.06, mat('#aaaaaa'), 4), 0.15, -0.23, 0));
    leg.add(at(sphere(0.008, matE(i % 2 ? M.navRed : M.navGreen, 0.9), 6, 6), 0.1, -0.03, 0));   // landing light
    leg.position.set(Math.cos(a) * 0.14, 0.1, Math.sin(a) * 0.14);
    leg.rotation.y = -a + Math.PI / 2;
    r.add(leg);
  }
  return r;
}

/** Delivered crate: strapped supply box with a GCPPA-ish label plate. */
export function buildDeliveryCrate(): THREE.Group {
  const c = group(box(0.12, 0.1, 0.12, mat('#7a6a50')));
  c.add(at(box(0.13, 0.01, 0.02, mat('#5a5040')), 0, 0.02, 0), at(box(0.02, 0.01, 0.13, mat('#5a5040')), 0, 0.02, 0));
  c.add(at(box(0.05, 0.03, 0.004, mat('#e8e0c8')), 0.02, 0.0, 0.062), at(box(0.004, 0.03, 0.05, mat('#c8a040')), -0.062, -0.01, 0.0));
  return c;
}

// ---------------------------------------------------------------------------
// PICKUP FERRY
// ---------------------------------------------------------------------------

/** Ferry: chamfered fuselage, raked cockpit, wings with VTOL pods, twin engines, cargo ramp, gear when landed. */
export function buildFerryBody(team: TeamPalette, mode: 'flight' | 'landed'): THREE.Group {
  const g = new THREE.Group();
  const landed = mode === 'landed';
  const y = landed ? 0.0 : 0.3;
  g.add(at(box(0.4, 0.25, 0.9, mat(team.primary)), 0, y + 0.3, 0));
  g.add(at(box(0.42, 0.04, 0.92, mat(team.accent)), 0, y + 0.19, 0), at(box(0.42, 0.04, 0.92, mat(team.accent)), 0, y + 0.41, 0));    // chine rails
  for (const sx of [-1, 1]) { g.add(at(plate(0.3, 0.1, team.detail), sx * 0.206, y + 0.3, 0.1, 0, sx * Math.PI / 2, 0)); g.add(at(box(0.01, 0.05, 0.42, mat(team.visor)), sx * 0.208, y + 0.33, 0.05)); }   // side plates + stripes
  const cockpit = at(box(0.32, 0.2, 0.25, mat(team.primary)), 0, y + 0.32, 0.55, -0.2); g.add(cockpit);
  g.add(at(box(0.3, 0.03, 0.26, mat(team.accent)), 0, y + 0.43, 0.55, -0.2));                   // cockpit roof trim
  g.add(at(box(0.28, 0.14, 0.02, mat(team.accent)), 0, y + 0.37, 0.675, -0.3), at(box(0.24, 0.1, 0.016, matE(team.visor, 0.5)), 0, y + 0.37, 0.685, -0.3));   // windshield frame + glass
  g.add(at(box(0.04, 0.26, 0.2, mat(team.detail)), 0, y + 0.5, -0.4), at(box(0.06, 0.03, 0.22, mat(team.accent)), 0, y + 0.63, -0.4));
  for (const sx of [-1, 1]) g.add(at(box(0.03, 0.14, 0.12, mat(team.detail)), sx * 0.18, y + 0.47, -0.42, 0, 0, sx * 0.3));          // twin outer fins
  for (const sx of [-1, 1]) {
    g.add(at(box(0.3, 0.03, 0.35, mat(team.accent)), sx * 0.35, y + 0.24, -0.1, 0, 0, sx * -0.15));
    g.add(at(box(0.06, 0.05, 0.15, mat(team.detail)), sx * 0.5, y + 0.2, -0.2));
    g.add(at(sphere(0.012, matE(sx < 0 ? M.navRed : M.navGreen, 0.9), 6, 6), sx * 0.53, y + 0.24, -0.12));   // nav lights
    g.add(at(cyl(0.06, 0.07, 0.12, mat(M.metal), 10), sx * 0.32, y + 0.14, 0.05), at(torus(0.065, 0.008, mat(M.metalDk), 6, 14), sx * 0.32, y + 0.2, 0.05, Math.PI / 2));   // VTOL pods
    g.add(at(cyl(0.04, 0.06, 0.04, mat(M.metalDk), 10), sx * 0.32, y + 0.07, 0.05), at(new THREE.Mesh(new THREE.CircleGeometry(0.035, 10), (landed ? mat(M.metalDk) : matE('#8ab4ff', 0.7))), sx * 0.32, y + 0.049, 0.05, Math.PI / 2));
    g.add(at(cyl(0.06, 0.08, 0.15, mat(M.metal), 10), sx * 0.12, y + 0.25, -0.52, Math.PI / 2), at(cyl(0.05, 0.075, 0.06, mat(M.metalDk), 10), sx * 0.12, y + 0.25, -0.58, Math.PI / 2));
    g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.045, 10), (landed ? mat(M.metalDk) : matE('#8ab4ff', 0.7))), sx * 0.12, y + 0.25, -0.611, Math.PI));   // engine glow
  }
  if (landed) {
    g.add(at(box(0.3, 0.02, 0.3, mat(team.accent)), 0, y + 0.05, -0.6, 0.3));                   // ramp down
    g.add(at(box(0.26, 0.14, 0.01, matE(team.visor, 0.35)), 0, y + 0.26, -0.455));              // lit bay
    for (const [x, z] of [[-0.18, 0.3], [0.18, 0.3], [-0.18, -0.3], [0.18, -0.3]]) g.add(at(cyl(0.015, 0.015, 0.18, mat(M.metal), 4), x, y + 0.09, z), at(cyl(0.04, 0.04, 0.015, mat(M.metalDk), 6), x, y + 0.008, z));
  } else {
    g.add(at(box(0.3, 0.18, 0.02, mat(team.accent)), 0, y + 0.26, -0.46), at(box(0.26, 0.012, 0.024, mat(team.detail)), 0, y + 0.26, -0.46));   // ramp closed + seam
  }
  g.add(at(cyl(0.006, 0.006, 0.15, mat(M.metal), 4), 0.08, y + 0.47, 0.2), at(plate(0.1, 0.04, team.detail), 0.0, y + 0.39, 0.1, -Math.PI / 2 + 0.01));   // antenna + roof plate
  return g;
}
